<template>
  <div class="import-custom-list">
    <label class="csv-file-label">CSV File</label>
    <input
      ref="fileUpload"
      type="file"
      id="custom-list-file-upload"
      @change="readCsvFile"
    />
    <div class="file-picker-row">
      <label for="custom-list-file-upload" class="file-picker">
        <span class="file-picker__btn">Choose File</span>
        <span class="file-picker__name">
          {{ fileName || 'No file chosen' }}
        </span>
      </label>
      <button
        v-if="fileObject && !loading"
        class="file-picker__delete"
        type="button"
        @click.stop="removeFile"
        aria-label="Remove selected file"
      >
        <img src="~@/assets/images/trash-2.svg" alt="Delete file" />
      </button>
    </div>

    <p v-if="loading" class="upload-status uploading">
      Uploading {{ fileName }} {{ loadedFilesSize }}/{{ fileSize }}
    </p>
    <p v-else-if="hasError" class="upload-status error">
      {{ errorMessage }}
    </p>
    <p v-else-if="fileObject" class="upload-status success">
      {{ fileName }} uploaded successfully
    </p>
    <p v-if="importValidationError" class="upload-status error">
      {{ importValidationError }}
    </p>

    <div v-if="fileObject && !loading && !hasError" class="pt-2">
      <div class="header-toggle my-2">
        <input
          id="file-has-header"
          type="checkbox"
          v-model="hasHeader"
        />
        <label for="file-has-header">First row is header</label>
      </div>
      <h2 class="map-title">Map CSV Columns</h2>
      <custom-list-mapping
        ref="customListMapping"
        :file-col="fileCol"
        @mapping-state="handleMappingState"
      />
      <div class="import-note">
        {{ rowsToImport }} row(s) will be imported
      </div>
    </div>

    <div class="import-actions pt-3">
      <button class="btn btn-cancel" @click="$emit('close')">
        Cancel
      </button>
      <button
        class="btn import-btn"
        :disabled="!canSave"
        @click="handleSave"
      >
        {{ submitting ? 'Importing...' : 'Import' }}
      </button>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import * as XLSX from 'xlsx'
import { generateS3SubmittingUrl, CreateCustomListImport } from '@/api'
import { alert, success } from '@/plugins/notification'
import CustomListMapping from './customListMapping.vue'

export default {
  name: 'ImportCustomList',
  components: { CustomListMapping },
  props: {
    customListId: {
      type: Number,
      required: true
    },
    parentId: {
      type: Number,
      default: null
    }
  },
  data () {
    return {
      notUploaded: true,
      loading: false,
      hasError: false,
      errorMessage: '',
      fileName: null,
      fileSize: 0,
      loadedFilesSize: 0,
      fileObject: null,
      fileCol: [],
      csvFirstRow: [],
      csvRows: [],
      hasHeader: true,
      mappingCanSave: false,
      mappingPayload: null,
      submitting: false,
      parsedLineCount: 0
    }
  },
  computed: {
    ...mapGetters(['openTenantId']),
    canSave () {
      return (
        Boolean(this.fileObject) &&
        !this.loading &&
        !this.hasError &&
        !this.submitting &&
        this.mappingCanSave &&
        !this.importValidationError
      )
    },
    rowsToImport () {
      return this.importValidation.count
    },
    importValidationError () {
      return this.importValidation.error
    },
    importValidation () {
      const valueColumnNumber = this.mappingPayload?.name?.columnNumber
      if (!valueColumnNumber || !this.csvRows.length) {
        return {
          count: 0,
          error: ''
        }
      }
      const valueColumnIndex = Number(valueColumnNumber) - 1
      const abbreviationColumnNumber = this.mappingPayload?.abbreviation?.columnNumber
      const abbreviationColumnIndex = abbreviationColumnNumber ? Number(abbreviationColumnNumber) - 1 : null
      const rows = this.hasHeader ? this.csvRows.slice(1) : this.csvRows
      const values = rows.map(row => (row[valueColumnIndex] || '').trim())
      const firstValueIndex = values.findIndex(Boolean)
      const lastValueIndex = values.reduce((lastIndex, value, index) => {
        return value ? index : lastIndex
      }, -1)
      const count = values.filter(Boolean).length
      const hasEmptyCellBetweenValues = firstValueIndex !== -1 &&
        values.slice(firstValueIndex, lastValueIndex + 1).some(value => !value)
      const abbreviationRowsWithoutValue = abbreviationColumnIndex === null
        ? []
        : rows.reduce((rowNumbers, row, index) => {
          const value = (row[valueColumnIndex] || '').trim()
          const abbreviation = (row[abbreviationColumnIndex] || '').trim()
          if (abbreviation && !value) {
            rowNumbers.push(index + (this.hasHeader ? 2 : 1))
          }
          return rowNumbers
        }, [])

      if (!count) {
        return {
          count,
          error: 'Value column must contain at least one value'
        }
      }

      if (abbreviationRowsWithoutValue.length) {
        return {
          count,
          error: `${this.formatRowNumbers(abbreviationRowsWithoutValue)} value is empty while abbreviation column has data`
        }
      }

      if (hasEmptyCellBetweenValues) {
        return {
          count,
          error: 'Value column cannot have empty cells between values'
        }
      }

      return {
        count,
        error: ''
      }
    }
  },
  methods: {
    readCsvFile (e) {
      const file = e.target.files[0]
      if (!file) return
      this.hasHeader = true
      this.mappingCanSave = false
      this.mappingPayload = null
      this.csvRows = []
      const reader = new FileReader()
      this.notUploaded = false
      this.fileName = file.name
      this.fileSize = file.size
      this.loading = true
      this.fileObject = file
      reader.onloadstart = () => {
        this.hasError = false
      }
      reader.onprogress = (ev) => {
        this.loadedFilesSize = ev.loaded
      }
      reader.onload = (ev) => {
        this.loadedFilesSize = ev.loaded
        this.readDone(ev.target.result)
      }
      reader.onerror = () => {
        this.errorMessage = 'Something went wrong'
        this.hasError = true
        this.loading = false
      }
      if (file.type === 'text/csv' || file.name.toLowerCase().endsWith('.csv')) {
        reader.readAsText(file)
      } else {
        this.errorMessage = 'Please upload a valid csv file'
        this.hasError = true
        this.loading = false
        this.fileObject = null
        this.fileCol = []
      }
    },
    readDone (csvData) {
      try {
        const rows = this.parseCsv(csvData)

        while (rows.length && rows[rows.length - 1].every(cell => !cell)) {
          rows.pop()
        }
        this.parsedLineCount = rows.length
        if (!rows.length) {
          throw new Error('empty-file')
        }
        this.csvRows = rows
        const firstRow = this.csvRows[0]
        if (firstRow.length < 2) {
          throw new Error('invalid-columns')
        }
        this.csvFirstRow = firstRow
        this.updateFileColumns()
        this.loading = false
        this.hasError = false
      } catch (e) {
        this.errorMessage = 'Please upload a valid csv file with name and abbreviation columns'
        this.hasError = true
        this.loading = false
        this.fileObject = null
        this.csvFirstRow = []
        this.csvRows = []
        this.fileCol = []
        this.parsedLineCount = 0
      }
      this.$refs.fileUpload.value = ''
    },
    parseCsv (csvData) {
      const workbook = XLSX.read(`sep=,\n${csvData.replace(/^\uFEFF/, '')}`, {
        type: 'string',
        raw: true
      })
      const worksheet = workbook.Sheets[workbook.SheetNames[0]]
      const rows = XLSX.utils.sheet_to_json(worksheet, {
        header: 1,
        defval: '',
        raw: true,
        blankrows: true
      })
      return rows.map(row => row.map(cell => String(cell).replace(/\s*[\r\n]+\s*/g, ' ').trim()))
    },
    updateFileColumns () {
      this.fileCol = this.csvFirstRow.map((value, index) => {
        const label = this.hasHeader && value ? value : `Column ${index + 1}`
        return {
          label,
          columnNumber: index + 1
        }
      })
    },
    handleMappingState ({ canSave, mapping }) {
      this.mappingCanSave = canSave
      this.mappingPayload = mapping
    },
    formatRowNumbers (rowNumbers) {
      const visibleRows = rowNumbers.slice(0, 6)
      const hiddenCount = rowNumbers.length - visibleRows.length
      const suffix = hiddenCount > 0 ? ` +${hiddenCount} more rows` : ''
      return `Row ${visibleRows.join(',')}${suffix}`
    },
    handleSave () {
      if (!this.canSave) return
      this.uploadAndImport()
    },
    async uploadAndImport () {
      if (!this.fileObject || !this.customListId || !this.mappingPayload) return
      this.submitting = true
      try {
        const { url } = await generateS3SubmittingUrl({
          tenantId: this.openTenantId,
          feature: 'customList',
          featureId: this.customListId,
          fileName: this.fileObject.name
        })
        await fetch(url, {
          method: 'PUT',
          body: this.fileObject,
          headers: {
            'Content-Type': this.fileObject.type || 'text/csv'
          }
        })
        const blobKey = url.split('?')[0].split('.com/').pop()
        const payload = {
          input: {
            blob_key: blobKey,
            custom_list_id: this.customListId,
            parent_id: this.parentId,
            mapping: this.mappingPayload,
            has_header: this.hasHeader
          }
        }
        const res = await CreateCustomListImport(payload)
        const message = res?.import_custom_list_values?.message || 'CSV import started successfully'
        success(message)
        this.$emit('imported')
        this.$emit('close')
      } catch (e) {
        const errMsg = e?.message?.replace('GraphQL error: ', '') || 'Unable to import custom list CSV'
        alert(errMsg)
      } finally {
        this.submitting = false
      }
    },
    removeFile () {
      this.notUploaded = true
      this.loading = false
      this.hasError = false
      this.errorMessage = ''
      this.fileName = null
      this.fileSize = 0
      this.loadedFilesSize = 0
      this.fileObject = null
      this.fileCol = []
      this.csvFirstRow = []
      this.csvRows = []
      this.hasHeader = true
      this.mappingCanSave = false
      this.mappingPayload = null
      this.parsedLineCount = 0
    }
  },
  watch: {
    hasHeader () {
      if (this.csvFirstRow.length) {
        this.updateFileColumns()
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.import-custom-list {
  padding: 5px 0 0 0;
  width: 560px;
  .import-actions {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: 12px;

    .btn {
      font-size: var(--text-s);
    }
  }
  input[type='file'] {
    display: none;
  }

  .csv-file-label {
    display: block;
    font-weight: 600;
    font-size: var(--text-s);
    color: #1f2937;
    margin-bottom: 8px;
  }

  .file-picker-row {
    display: flex;
    align-items: stretch;
    gap: 8px;
  }

  .file-picker {
    display: flex;
    align-items: center;
    flex: 1;
    width: 100%;
    min-height: 34px;
    background: #f3f4f6;
    border: 1px solid #e5e7eb;
    border-radius: 4px;
    cursor: pointer;
    overflow: hidden;

    &__btn {
      display: inline-flex;
      align-items: center;
      height: 100%;
      padding: 8px 10px;
      color: #111827;
      font-size: var(--text-xs2);
      border-right: 1px solid #e5e7eb;
      background: #f3f4f6;
      white-space: nowrap;
    }

    &__name {
      padding: 8px 10px;
      color: #111827;
      font-size: var(--text-xs2);
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    &__delete {
      min-width: 34px;
      min-height: 34px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      border: 1px solid #e5e7eb;
      border-radius: 4px;
      background: #f9fafb;
      color: #6b7280;
      cursor: pointer;
      padding: 0 10px;
    }

    &__delete:hover {
      background: #f3f4f6;
      color: #374151;
    }

    &__delete img {
      width: 14px;
      height: 14px;
      opacity: 0.75;
    }
  }

  .upload-status {
    margin: 8px 0 0;
    font-size: var(--text-xs1);
    &.success {
      color: #16a34a;
    }
    &.error {
      color: #dc2626;
    }
    &.uploading {
      color: #6b7280;
    }
  }

  .header-toggle {
    display: flex;
    align-items: center;
    gap: 8px;
    background: #f9fafb;
    border: 1px solid #e5e7eb;
    border-radius: 6px;
    padding: 10px 12px;
    margin-top: 12px;

    label {
      margin: 0;
      color: #374151;
      font-size: var(--text-s);
      cursor: pointer;
    }

    input {
      appearance: none;
      width: 15px;
      height: 15px;
      margin: 0;
      border: 1px solid #d1d5db;
      border-radius: 4px;
      background: #fff;
      display: inline-grid;
      place-content: center;
      cursor: pointer;
    }

    input::before {
      content: '';
      width: 8px;
      height: 8px;
      transform: scale(0);
      transition: transform 0.12s ease-in-out;
      clip-path: polygon(14% 44%, 0 65%, 50% 100%, 100% 16%, 80% 0%, 43% 62%);
      background-color: #ffffff;
    }

    input:checked {
      background: var(--brand-color-1);
      border-color: var(--brand-color-1);
    }

    input:checked::before {
      transform: scale(1);
    }
  }

  .map-title {
    margin: 16px 0 10px;
    font-size: var(--text-s);
    font-weight: 600;
    color: #1f2937;
  }

  .import-note {
    margin-top: 12px;
    background: #eef3fb;
    border-radius: 6px;
    padding: 10px 12px;
    color: #2563eb;
    font-size: var(--text-xs1);
  }

  .import-btn:disabled {
    opacity: 0.6;
  }

  .import-btn {
    min-width: 84px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }

  .import-btn.loading {
    pointer-events: none;
  }

  .import-btn__loader {
    width: 16px;
    height: 16px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }

  .import-btn__loader ::v-deep .loading-circle {
    font-size: 16px;
  }
}
</style>
